import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
  saveErrorReason,
  withFailureReasons,
  type BatchFailure,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useProjectAttachmentsList } from "../api/useProjectAttachmentsList";
import { useProjectAttachmentMutations } from "../api/useProjectAttachmentMutations";
import { useDownloadAttachment } from "../api/useDownloadAttachment";
import { buildProjectAttachmentBatchActions } from "./batchActions";
import type { ProjectAttachment, ProjectAttachmentTab } from "../types";

export interface ListProps {
  projectId: string;
  canManage: boolean;
}

const List = ({ projectId, canManage }: ListProps): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ProjectAttachmentTab>("active");
  const sel = useBatchSelection([tab]);
  const { archive, restore } = useProjectAttachmentMutations();
  const download = useDownloadAttachment();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useProjectAttachmentsList({
      projectId,
      active: tab === "active",
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const handleDownload = async (a: ProjectAttachment) => {
    try {
      await download(a.id, a.document);
    } catch {
      setToast({
        variant: "danger",
        text: "Could not download the attachment.",
      });
    }
  };

  const columns: ColumnDef<ProjectAttachment>[] = [
    {
      accessorKey: "document",
      header: "Document",
      cell: ({ row }) => row.original.document,
    },
    {
      accessorKey: "attachmentTypeName",
      header: "Attachment Type",
      cell: ({ row }) => row.original.attachmentTypeName || "—",
    },
    {
      id: "download",
      header: "Download",
      cell: ({ row }) => (
        <Button
          variant="link"
          className="p-0"
          aria-label={`Download ${row.original.document}`}
          onClick={() => handleDownload(row.original)}
        >
          <FontAwesomeIcon icon={faDownload} />
        </Button>
      ),
    },
  ];

  const runOnSelected = async (
    fn: (a: ProjectAttachment) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const a of targets) {
      try {
        await fn(a);
      } catch (err) {
        failures.push({ label: a.document, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} attachment(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (a) => archive(a.id),
        (n) => `${n} attachment(s) archived.`
      );
    } else {
      await runOnSelected(
        (a) => restore(a.id, projectId),
        (n) => `${n} attachment(s) restored.`
      );
    }
  };

  const batchActions = canManage
    ? buildProjectAttachmentBatchActions(tab, {
        onDelete: () => setConfirmOpen(true),
        onRestore: () => setConfirmOpen(true),
      })
    : [];

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Attachments"
        onNew={canManage ? () => navigate("new") : undefined}
        newButtonLabel="+ Add an Attachment"
        batchActions={batchActions}
        selectedCount={canManage ? sel.selectedIds.length : 0}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as ProjectAttachmentTab);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectable={canManage}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={canManage ? (id) => navigate(`${id}/edit`) : undefined}
          emptyMessage="No attachments found."
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active" ? "Archive attachments?" : "Restore attachments?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} attachment(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
